/**
 * DmnGenerateModal.js — DMN wizard, two-phase version
 * (THEMIS_BACKEND_COMPLETION_PLAN B6).
 *
 * Replaces the legacy "single-shot generate-dmn" flow. New shape:
 *
 *   1. Origen          — Descripción libre + dropzone (PDF/DOCX/TXT) + voz
 *                        (mismo layout que el wizard de Atlas/BPMN).
 *   2. Outline editor  — Markdown DMN outline + lint en vivo,
 *                        con vista renderizada con colores y modo edición.
 *
 * On Apply:
 *   1. POST /dmn-outline-to-xml   → DMN 1.3 XML (deterministic, no LLM)
 *   2. epxDmn.importXML(xml)
 *   3. Si runAfterApply y hay escenarios en el outline:
 *      epxDmn.simulator.loadScenarios(scenarios) → runAll() → resumen al chat
 *
 * The outline format is documented in
 * api/prompts/dmn_outline_system.txt + plan §3.2.
 */

import {
  dmnOutline,
  dmnOutlineLint,
  dmnOutlineToXml,
  extractDmnDocument,
  transcribe,
} from './aiApi.js';
import { AiVoice } from './AiVoice.js';
import { groupFriendly } from './friendlyIssueMap.js';
import { renderFriendlyDmnOutline } from './DmnFriendlyPreview.js';

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

// ── Markdown renderer with token colours ───────────────────────────────────
// Lightweight: handles headers, bullets, bold/italic/code, key:value,
// strings, numbers, booleans and pipe tables. No external dep.

function _esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Tokens are extracted from the original string in priority order with their
// (start, end) offsets, then assembled into HTML. This avoids the "self-eating"
// pattern of sequential .replace() calls (the previous string-after-bold pass
// matched the quotes inside class="md-bold">, rendering literal `"md-bold">`
// text in the preview — see Apr-29 bug report).
const _MD_RULES = [
  { re: /`([^`]+)`/g,                     cls: 'md-code',   esc: true,  wrap: '' },
  { re: /\*\*([^*\n]+)\*\*/g,             cls: 'md-bold',   esc: true,  wrap: '' },
  { re: /\*([^*\n]+?)\*/g,                cls: 'md-italic', esc: true,  wrap: '' },
  { re: /"([^"\n]*)"/g,                   cls: 'md-string', esc: true,  wrap: '"' },
  { re: /\b(-?\d+(?:\.\d+)?)\b/g,         cls: 'md-number', esc: false, wrap: '' },
  { re: /\b(true|false|null)\b/g,         cls: 'md-bool',   esc: false, wrap: '' },
];

function _renderInline(raw) {
  const s = String(raw ?? '');
  if (!s) return '';

  // Collect non-overlapping matches in priority order.
  const matches = [];
  for (const rule of _MD_RULES) {
    rule.re.lastIndex = 0;
    let m;
    while ((m = rule.re.exec(s)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      // Skip if it overlaps any earlier (higher-priority) match.
      if (matches.some((x) => x.start < end && x.end > start)) continue;
      matches.push({ start, end, content: m[1], rule });
    }
  }
  matches.sort((a, b) => a.start - b.start);

  let out = '';
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) out += _esc(s.slice(cursor, m.start));
    const inner = m.rule.esc ? _esc(m.content) : m.content;
    out += `<span class="${m.rule.cls}">${m.rule.wrap}${inner}${m.rule.wrap}</span>`;
    cursor = m.end;
  }
  if (cursor < s.length) out += _esc(s.slice(cursor));
  return out;
}

function _renderLine(line) {
  if (!line.trim()) return '<div class="md-empty"></div>';

  const heading = line.match(/^(#{1,6})\s+(.*)$/);
  if (heading) {
    const lvl = heading[1].length;
    return `<div class="md-h md-h${lvl}">${_renderInline(heading[2])}</div>`;
  }

  const bullet = line.match(/^(\s*)([-*])\s+(.*)$/);
  if (bullet) {
    const indent = bullet[1].length;
    let body = bullet[3];
    // key: value (only when the colon is reasonably early, not inside a sentence)
    const kv = body.match(/^([^:`*]{1,60}?):\s+(.+)$/);
    let html;
    if (kv) {
      html = `<span class="md-key">${_renderInline(kv[1])}:</span> ${_renderInline(kv[2])}`;
    } else {
      html = _renderInline(body);
    }
    return `<div class="md-li" style="padding-left:${indent * 8 + 14}px">`
         + `<span class="md-bullet" aria-hidden="true">•</span> ${html}`
         + '</div>';
  }

  if (/^\s*\|/.test(line)) {
    // pipe table row — also covers separator rows like "| --- | --- |"
    const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    const cells = trimmed.split('|').map((c) => c.trim());
    const isSep = cells.every((c) => /^:?-+:?$/.test(c));
    if (isSep) return '<div class="md-tr md-tr--sep"></div>';
    return `<div class="md-tr">${
      cells.map((c) => `<span class="md-td">${_renderInline(c)}</span>`).join('')
    }</div>`;
  }

  return `<div class="md-p">${_renderInline(line)}</div>`;
}

export function renderDmnMarkdown(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  return lines.map(_renderLine).join('');
}

export class DmnGenerateModal {
  constructor() {
    this._modal       = null;
    this._panel       = null;
    this._epxDmn      = null;
    this._language    = 'es';
    this._onApply     = null;
    this._uploadedDoc = null;
    this._abortCtl    = null;
    this._currentStep = 1;
    this._outlineText = '';
    this._lintIssues  = [];
    this._lintSummary = { errors: 0, warnings: 0, info: 0 };
    this._lastOutline = null;            // parsed outline (for scenarios)
    this._runAfterApply = false;
    this._viewMode    = 'friendly';       // 'friendly' | 'preview' | 'edit'
    this._issuesViewMode = 'friendly';    // 'friendly' | 'technical'
    this._lintDebounced = debounce(() => this._runLint(), 350);
    this._voice = new AiVoice();
    this._lastSourcePayload = '';         // remembered for "Arreglar errores"
    this._lastSourceKind    = 'text';     // 'text' | 'document'
    this._lastDocumentId    = null;
    this._fixing = false;
  }

  mount(_containerEl, opts = {}) {
    this._panel    = opts.panel    || this._panel;
    this._epxDmn   = opts.epxDmn   || this._epxDmn;
    this._language = opts.language || this._language;
    this._onApply  = typeof opts.onApply === 'function' ? opts.onApply : null;

    if (this._mounted) return;
    this._mounted = true;

    this._modal = document.getElementById('ai-dmn-gen-modal');
    if (!this._modal) {
      console.warn('[DmnGenerateModal] #ai-dmn-gen-modal not found in DOM');
      return;
    }

    // Close
    this._modal.querySelector('.ai-gen-modal__backdrop')
      ?.addEventListener('click', () => this.close());
    document.getElementById('ai-dmn-gen-modal-close')
      ?.addEventListener('click', () => this.close());
    document.getElementById('ai-dmn-gen-cancel')
      ?.addEventListener('click', () => this.close());

    // Step 1 enable
    document.getElementById('ai-dmn-gen-input')
      ?.addEventListener('input', () => this._refreshStep1Submit());

    // Dropzone + voice
    this._mountDropzone();
    this._mountVoice();

    // Step 1 → 2
    document.getElementById('ai-dmn-gen-submit')
      ?.addEventListener('click', () => this._submitStep1());

    // Step 2 — outline editor live lint
    document.getElementById('ai-dmn-gen-outline-editor')
      ?.addEventListener('input', (ev) => {
        this._outlineText = ev.target.value;
        this._renderPreview();
        // The friendly view depends on _lastOutline (parsed). We refresh after
        // _runLint completes; in the meantime keep showing the previous render.
        this._lintDebounced();
      });

    // Step 2 — view tabs (friendly / preview / edit)
    this._modal.querySelectorAll('.ai-dmn-gen-view-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = btn.dataset.view;
        if (v) this._setViewMode(v);
      });
    });

    // Step 2 — issues view tabs (friendly / technical)
    this._modal.querySelectorAll('.ai-dmn-gen-issues-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = btn.dataset.issuesView;
        if (v) this._setIssuesViewMode(v);
      });
    });

    // Back / fix / apply
    document.getElementById('ai-dmn-gen-back-2')
      ?.addEventListener('click', () => this._goStep(1));
    document.getElementById('ai-dmn-gen-run-after-apply')
      ?.addEventListener('change', (ev) => {
        this._runAfterApply = !!ev.target.checked;
      });
    document.getElementById('ai-dmn-gen-fix')
      ?.addEventListener('click', () => this._fixIssues());
    document.getElementById('ai-dmn-gen-apply')
      ?.addEventListener('click', () => this._apply());

    // Apply confirm overlay buttons
    document.getElementById('ai-dmn-gen-apply-confirm-cancel')
      ?.addEventListener('click', () => this._closeApplyConfirm());
    document.getElementById('ai-dmn-gen-apply-confirm-fix')
      ?.addEventListener('click', () => {
        this._closeApplyConfirm();
        this._fixIssues();
      });
    document.getElementById('ai-dmn-gen-apply-confirm-force')
      ?.addEventListener('click', () => {
        this._closeApplyConfirm();
        this._doApply();
      });
  }

  open() {
    if (!this._modal) return;
    this._reset();
    this._modal.hidden = false;
    document.getElementById('ai-dmn-gen-input')?.focus();
  }

  close() {
    if (this._abortCtl) {
      try { this._abortCtl.abort(); } catch (_) { /* noop */ }
      this._abortCtl = null;
    }
    try { this._voice.stopRecording(); } catch (_) { /* noop */ }
    if (this._modal) this._modal.hidden = true;
  }

  // ── Step rendering ──────────────────────────────────────────────────────

  _reset() {
    this._currentStep   = 1;
    this._uploadedDoc   = null;
    this._outlineText   = '';
    this._lintIssues    = [];
    this._lastOutline   = null;
    this._runAfterApply = false;
    this._viewMode      = 'friendly';
    this._issuesViewMode = 'friendly';
    this._lastSourcePayload = '';
    this._lastSourceKind    = 'text';
    this._lastDocumentId    = null;
    this._fixing        = false;

    const ta = document.getElementById('ai-dmn-gen-input');
    if (ta) ta.value = '';
    const editor = document.getElementById('ai-dmn-gen-outline-editor');
    if (editor) editor.value = '';
    const preview = document.getElementById('ai-dmn-gen-outline-preview');
    if (preview) preview.innerHTML = '';
    const friendly = document.getElementById('ai-dmn-gen-outline-friendly');
    if (friendly) friendly.innerHTML = '';
    const filesEl = document.getElementById('ai-dmn-gen-files');
    if (filesEl) filesEl.innerHTML = '';
    const fileInput = document.getElementById('ai-dmn-gen-file');
    if (fileInput) fileInput.value = '';
    const runChk = document.getElementById('ai-dmn-gen-run-after-apply');
    if (runChk) runChk.checked = false;

    this._closeApplyConfirm();
    this._setViewMode('friendly');
    this._setIssuesViewMode('friendly');
    this._goStep(1);
    this._refreshStep1Submit();
  }

  _goStep(n) {
    this._currentStep = n;
    [1, 2, 5].forEach((i) => {
      const el = document.getElementById(`ai-dmn-gen-step-${i}`);
      if (el) el.hidden = (i !== n);
    });
    const box = this._modal?.querySelector('.ai-gen-modal__box');
    if (box) box.classList.toggle('is-wide', n === 2);
  }

  _refreshStep1Submit() {
    const submitBtn = document.getElementById('ai-dmn-gen-submit');
    if (!submitBtn) return;
    const text = (document.getElementById('ai-dmn-gen-input')?.value || '').trim();
    const hasDoc = !!(this._uploadedDoc && this._uploadedDoc.text && this._uploadedDoc.text.trim().length > 0);
    submitBtn.disabled = !(text.length > 0 || hasDoc);
  }

  _refreshApplyButton() {
    const applyBtn = document.getElementById('ai-dmn-gen-apply');
    if (applyBtn) {
      // Apply is always available as long as there is outline text. Lint
      // errors no longer block the user — they're surfaced as a tooltip and
      // the backend still has the final word via metadata.blocked.
      applyBtn.disabled = !this._outlineText.trim();
      const errs = this._lintSummary.errors || 0;
      applyBtn.title = errs > 0
        ? `Hay ${errs} error(es) en el outline — el backend puede rechazar la generación.`
        : '';
    }
    const fixBtn = document.getElementById('ai-dmn-gen-fix');
    if (fixBtn) {
      const hasIssues = (this._lintSummary.errors || 0) > 0 || (this._lintSummary.warnings || 0) > 0;
      fixBtn.hidden = !hasIssues;
      fixBtn.disabled = !!this._fixing || !this._outlineText.trim();
    }
  }

  _setViewMode(mode) {
    if (mode !== 'friendly' && mode !== 'preview' && mode !== 'edit') return;
    this._viewMode = mode;
    const friendly = document.getElementById('ai-dmn-gen-outline-friendly');
    const preview  = document.getElementById('ai-dmn-gen-outline-preview');
    const editor   = document.getElementById('ai-dmn-gen-outline-editor');
    if (friendly) friendly.hidden = (mode !== 'friendly');
    if (preview)  preview.hidden  = (mode !== 'preview');
    if (editor)   editor.hidden   = (mode !== 'edit');
    this._modal?.querySelectorAll('.ai-dmn-gen-view-tab').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.view === mode);
    });
    if (mode === 'friendly') {
      this._renderFriendly();
    } else if (mode === 'preview') {
      this._renderPreview();
    } else {
      // entering edit mode → keep textarea in sync with current text
      if (editor) editor.value = this._outlineText;
      editor?.focus();
    }
  }

  _setIssuesViewMode(mode) {
    if (mode !== 'friendly' && mode !== 'technical') return;
    this._issuesViewMode = mode;
    this._modal?.querySelectorAll('.ai-dmn-gen-issues-tab').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.issuesView === mode);
    });
    this._renderIssues();
  }

  _renderPreview() {
    const preview = document.getElementById('ai-dmn-gen-outline-preview');
    if (!preview) return;
    if (!this._outlineText.trim()) {
      preview.innerHTML = '<div class="ai-dmn-gen-outline-preview__empty">— sin contenido —</div>';
      return;
    }
    preview.innerHTML = renderDmnMarkdown(this._outlineText);
  }

  _renderFriendly() {
    const friendly = document.getElementById('ai-dmn-gen-outline-friendly');
    if (!friendly) return;
    if (!this._outlineText.trim() || !this._lastOutline) {
      friendly.innerHTML = '<div class="dmn-fp__empty">— sin contenido —</div>';
      return;
    }
    friendly.innerHTML = renderFriendlyDmnOutline(this._lastOutline, this._language);
  }

  // ── Dropzone (PDF/DOCX/TXT) ─────────────────────────────────────────────

  _mountDropzone() {
    const dropzone = document.getElementById('ai-dmn-gen-dropzone');
    const fileInput = document.getElementById('ai-dmn-gen-file');
    if (!dropzone || !fileInput) return;

    const MAX_BYTES = 25 * 1024 * 1024;

    const ingest = async (file) => {
      if (!file) return;
      if (file.size > MAX_BYTES) {
        alert(`"${file.name}" supera 25 MB — se omite.`);
        return;
      }

      this._uploadedDoc = { name: file.name, text: '', pages: 0 };
      const filesEl = document.getElementById('ai-dmn-gen-files');
      if (filesEl) {
        filesEl.innerHTML = `
          <li class="is-loading">
            <span class="ai-gen-file__name" title="${file.name}">📄 ${file.name}</span>
            <span class="ai-gen-file__meta">${Math.round(file.size / 1024)} KB · extrayendo…</span>
          </li>`;
      }

      try {
        const res = await extractDmnDocument(file);
        this._uploadedDoc = {
          document_id: res.document_id,
          name: file.name,
          text: res.text || '',
          pages: res.pages || 0,
        };
        if (filesEl) {
          const hint = `${Math.round(file.size / 1024)} KB · ${res.pages || '?'} pág · ${(res.text || '').length} chars`;
          filesEl.innerHTML = `
            <li>
              <span class="ai-gen-file__name" title="${file.name}">📄 ${file.name}</span>
              <span class="ai-gen-file__meta">${hint}</span>
              <button type="button" class="ai-gen-file__remove" title="Quitar">✕</button>
            </li>`;
          filesEl.querySelector('.ai-gen-file__remove')?.addEventListener('click', () => {
            this._uploadedDoc = null;
            filesEl.innerHTML = '';
            this._refreshStep1Submit();
          });
        }
      } catch (err) {
        if (filesEl) {
          filesEl.innerHTML = `
            <li class="is-error">
              <span class="ai-gen-file__name" title="${file.name}">📄 ${file.name}</span>
              <span class="ai-gen-file__meta">Error: ${err.message || err}</span>
            </li>`;
        }
        this._uploadedDoc = null;
      } finally {
        this._refreshStep1Submit();
      }
    };

    dropzone.addEventListener('click', (ev) => {
      if (ev.target.closest('.ai-gen-file__remove')) return;
      if (ev.target === fileInput) return;
      fileInput.click();
    });
    dropzone.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        fileInput.click();
      }
    });
    fileInput.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      ingest(file);
      fileInput.value = '';
    });
    dropzone.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      dropzone.classList.add('is-dragging');
    });
    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragging'));
    dropzone.addEventListener('drop', (ev) => {
      ev.preventDefault();
      dropzone.classList.remove('is-dragging');
      const file = ev.dataTransfer?.files?.[0];
      ingest(file);
    });
  }

  // ── Voice (mic) ─────────────────────────────────────────────────────────

  _mountVoice() {
    const micBtn = document.getElementById('ai-dmn-gen-mic');
    if (!micBtn) return;
    const textarea = document.getElementById('ai-dmn-gen-input');

    if (!this._voice.isSupported()) {
      micBtn.disabled = true;
      micBtn.title = 'Tu navegador no soporta grabación de voz';
      return;
    }

    const micLabel = micBtn.querySelector('.ai-mic-label');
    micBtn.addEventListener('click', async () => {
      if (this._voice.isRecording()) {
        this._voice.stopRecording();
        micBtn.classList.remove('is-recording');
        if (micLabel) micLabel.textContent = 'Grabar voz';
        return;
      }
      micBtn.classList.add('is-recording');
      if (micLabel) micLabel.textContent = 'Detener grabación';
      try {
        await this._voice.startRecording(async (blob) => {
          micBtn.classList.remove('is-recording');
          micBtn.classList.add('is-transcribing');
          if (micLabel) micLabel.textContent = 'Transcribiendo…';
          micBtn.disabled = true;
          try {
            const text = await transcribe(blob);
            if (textarea) {
              textarea.value = (textarea.value ? textarea.value + ' ' : '') + text;
              textarea.dispatchEvent(new Event('input'));
            }
          } catch (err) {
            alert(`Error al transcribir: ${err.message || err}`);
          } finally {
            micBtn.classList.remove('is-transcribing');
            if (micLabel) micLabel.textContent = 'Grabar voz';
            micBtn.disabled = false;
          }
        });
      } catch (err) {
        micBtn.classList.remove('is-recording');
        if (micLabel) micLabel.textContent = 'Grabar voz';
        alert(`Micrófono no disponible: ${err.message || err}`);
      }
    });
  }

  // ── Step 1 → 2 ──────────────────────────────────────────────────────────

  _collectStep1() {
    const text = (document.getElementById('ai-dmn-gen-input')?.value || '').trim();
    const doc = this._uploadedDoc || null;
    const docText = doc && doc.text ? doc.text.trim() : '';

    if (text && docText) {
      // Both present → text + document context concatenated
      return {
        source: 'document',
        payload: `${text}\n\n---\n${docText}`,
        document_id: doc?.document_id || null,
      };
    }
    if (docText) {
      return {
        source: 'document',
        payload: docText,
        document_id: doc?.document_id || null,
      };
    }
    return { source: 'text', payload: text, document_id: null };
  }

  async _submitStep1() {
    const step1 = this._collectStep1();
    if (!step1.payload) return;

    this._lastSourcePayload = step1.payload;
    this._lastSourceKind    = step1.source;
    this._lastDocumentId    = step1.document_id;

    this._goStep(5); // loading
    if (this._abortCtl) try { this._abortCtl.abort(); } catch (_) { /* noop */ }
    this._abortCtl = new AbortController();

    try {
      const result = await dmnOutline(
        {
          source: step1.source,
          payload: step1.payload,
          language: this._language,
          include_scenarios: true,
          document_id: step1.document_id,
        },
        this._abortCtl.signal,
      );
      this._applyOutlineResult(result);
      this._goStep(2);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._goStep(1);
      alert(`No se pudo generar el outline: ${err.message || err}`);
    } finally {
      this._abortCtl = null;
    }
  }

  _applyOutlineResult(result) {
    this._outlineText = result.outline_text || '';
    this._lastOutline = result.outline || null;
    this._lintIssues  = result.issues || [];
    this._lintSummary = (result.metadata && result.metadata.summary)
      || this._summarize(this._lintIssues);
    const editor = document.getElementById('ai-dmn-gen-outline-editor');
    if (editor) editor.value = this._outlineText;
    this._renderFriendly();
    this._renderPreview();
    this._renderIssues();
    this._refreshApplyButton();
    const msgEl = document.getElementById('ai-dmn-gen-message');
    if (msgEl) msgEl.textContent = result.message || '';
  }

  // ── Step 2: live lint ───────────────────────────────────────────────────

  async _runLint() {
    if (!this._outlineText.trim()) {
      this._lintIssues  = [];
      this._lintSummary = { errors: 0, warnings: 0, info: 0 };
      this._lastOutline = null;
      this._renderFriendly();
      this._renderIssues();
      this._refreshApplyButton();
      return;
    }
    try {
      const res = await dmnOutlineLint(this._outlineText, this._language);
      this._lintIssues  = res.issues || [];
      this._lintSummary = res.summary || this._summarize(this._lintIssues);
      this._lastOutline = res.outline || null;
      this._renderFriendly();
      this._renderIssues();
      this._refreshApplyButton();
    } catch (err) {
      console.warn('[DmnGenerateModal] lint failed:', err);
    }
  }

  _summarize(issues) {
    return issues.reduce((acc, i) => {
      const k = i.severity === 'error' ? 'errors'
              : i.severity === 'warning' ? 'warnings' : 'info';
      acc[k]++;
      return acc;
    }, { errors: 0, warnings: 0, info: 0 });
  }

  _renderIssues() {
    const el = document.getElementById('ai-dmn-gen-issues');
    if (!el) return;
    if (!this._lintIssues.length) {
      el.innerHTML = `<p class="ai-dmn-gen-issues-empty">✓ Sin problemas detectados.</p>`;
      return;
    }
    if (this._issuesViewMode === 'technical') {
      el.innerHTML = this._renderIssuesTechnical(this._lintIssues);
    } else {
      el.innerHTML = this._renderIssuesFriendly(this._lintIssues);
    }
  }

  _renderIssuesTechnical(issues) {
    const sevIcon = (s) => s === 'error' ? '🔴' : s === 'warning' ? '🟡' : 'ℹ️';
    return `
      <ul class="ai-dmn-gen-issues-list">
        ${issues.map((i) => `
          <li class="ai-dmn-gen-issue is-${_esc(i.severity)}" data-issue-code="${_esc(i.code)}">
            <span class="ai-dmn-gen-issue-icon">${sevIcon(i.severity)}</span>
            <span class="ai-dmn-gen-issue-code">${_esc(i.code)}</span>
            <span class="ai-dmn-gen-issue-msg">${_esc(i.message)}</span>
            ${i.suggestion ? `<span class="ai-dmn-gen-issue-tip">💡 ${_esc(i.suggestion)}</span>` : ''}
          </li>
        `).join('')}
      </ul>`;
  }

  _renderIssuesFriendly(issues) {
    const { groups, hidden } = groupFriendly(issues, this._language);
    if (groups.length === 0) {
      const allHiddenMsg = hidden > 0
        ? (this._language === 'en'
            ? `✓ Nothing to review for now. <button type="button" class="ai-dmn-gen-issues-tech-link" data-issues-view="technical">Show technical details (${hidden})</button>`
            : `✓ Nada para revisar ahora. <button type="button" class="ai-dmn-gen-issues-tech-link" data-issues-view="technical">Ver detalles técnicos (${hidden})</button>`)
        : (this._language === 'en' ? '✓ Nothing to review.' : '✓ Sin observaciones.');
      // Wire the inline link to the technical tab.
      const html = `<p class="ai-dmn-gen-issues-empty">${allHiddenMsg}</p>`;
      // Defer the binding to a microtask so the innerHTML is in the DOM first.
      queueMicrotask(() => {
        document.querySelector('.ai-dmn-gen-issues-tech-link')
          ?.addEventListener('click', () => this._setIssuesViewMode('technical'));
      });
      return html;
    }
    const sevIcon = (s) => s === 'error' ? '✗' : s === 'warning' ? '⚠' : 'ℹ';
    const groupsHtml = groups.map((g) => {
      const total = g.issues.length;
      const errors = g.issues.filter((i) => i.severity === 'error').length;
      const warns  = g.issues.filter((i) => i.severity === 'warning').length;
      const summary = errors > 0
        ? (this._language === 'en' ? `${total} to review` : `${total} a revisar`)
        : warns > 0
          ? (this._language === 'en' ? `${total} suggestion${total === 1 ? '' : 's'}` : `${total} sugerencia${total === 1 ? '' : 's'}`)
          : '';
      return `
        <section class="ai-dmn-gen-issues-group">
          <header class="ai-dmn-gen-issues-group__header">
            <span class="ai-dmn-gen-issues-group__title">${_esc(g.title)}</span>
            ${summary ? `<span class="ai-dmn-gen-issues-group__count">${_esc(summary)}</span>` : ''}
          </header>
          <ul class="ai-dmn-gen-issues-list">
            ${g.issues.map((f) => `
              <li class="ai-dmn-gen-issue is-${_esc(f.severity)}" data-issue-code="${_esc(f.code)}">
                <span class="ai-dmn-gen-issue-icon" aria-hidden="true">${sevIcon(f.severity)}</span>
                <div class="ai-dmn-gen-issue-content">
                  <p class="ai-dmn-gen-issue-title">${_esc(f.title)}</p>
                  ${f.body ? `<p class="ai-dmn-gen-issue-body">${_esc(f.body)}</p>` : ''}
                  ${f.suggestion ? `<p class="ai-dmn-gen-issue-tip">💡 ${_esc(f.suggestion)}</p>` : ''}
                </div>
              </li>
            `).join('')}
          </ul>
        </section>`;
    }).join('');

    const hiddenLine = hidden > 0
      ? `<p class="ai-dmn-gen-issues-hidden-note">
           <button type="button" class="ai-dmn-gen-issues-tech-link" data-issues-view="technical">
             ${this._language === 'en'
               ? `+ ${hidden} technical detail${hidden === 1 ? '' : 's'} (show)`
               : `+ ${hidden} detalle${hidden === 1 ? '' : 's'} técnico${hidden === 1 ? '' : 's'} (ver)`}
           </button>
         </p>`
      : '';

    queueMicrotask(() => {
      document.querySelector('.ai-dmn-gen-issues-tech-link')
        ?.addEventListener('click', () => this._setIssuesViewMode('technical'));
    });

    return groupsHtml + hiddenLine;
  }

  // ── "Arreglar errores" — re-llamar al LLM con outline + issues ──────────

  async _fixIssues() {
    if (!this._outlineText.trim() || this._fixing) return;
    if (!this._lintIssues.length) return;

    const issuesText = this._lintIssues.map((i, idx) => {
      const sev = (i.severity || 'info').toUpperCase();
      const tip = i.suggestion ? `\n   sugerencia: ${i.suggestion}` : '';
      const where = i.line ? ` (línea ${i.line})` : '';
      return `${idx + 1}. [${sev}] ${i.code}${where}: ${i.message}${tip}`;
    }).join('\n');

    const lang = this._language === 'en' ? 'en' : 'es';
    const header = lang === 'en'
      ? [
          'TASK: fix a DMN Markdown outline. The outline below was generated',
          'previously and the deterministic linter reported the issues listed',
          'after it. Apply the minimum changes that resolve every listed issue.',
          '',
          'Rules:',
          '- Preserve the original business logic and decision names.',
          '- Add a "## Inputs" entry for every name referenced from a decision.',
          '- For INPUT_REF_UNKNOWN: add the missing input OR rename the column',
          '  to match an existing input/decision/BKM.',
          '- For DEPENDENCY_UNKNOWN: declare the dependency as Input, Decision,',
          '  or BKM in the outline (whichever fits the business meaning).',
          '- For TABLE_TOO_NARROW: add the missing input or output column.',
          '- For TABLE_MISSING_FALLBACK: add a final rule with all inputs set',
          '  to "-" so the table always matches.',
          '- Output ONLY the corrected outline (Markdown). No prose, no fences.',
        ].join('\n')
      : [
          'TAREA: corregir un outline DMN en Markdown. El outline de abajo fue',
          'generado antes y el linter determinístico encontró los problemas',
          'listados después. Aplicá los cambios mínimos que resuelvan cada uno.',
          '',
          'Reglas:',
          '- Mantené la lógica de negocio y los nombres de decisiones originales.',
          '- Declará en "## Inputs" todo nombre referenciado desde una decisión.',
          '- INPUT_REF_UNKNOWN → agregá el input faltante O renombrá la columna',
          '  para que coincida con un input/decisión/BKM existente.',
          '- DEPENDENCY_UNKNOWN → declará esa dependencia como Input, Decisión',
          '  o BKM en el outline (lo que mejor refleje el negocio).',
          '- TABLE_TOO_NARROW → agregá la columna de input/output que falta.',
          '- TABLE_MISSING_FALLBACK → agregá una regla final con todos los inputs',
          '  en "-" para que la tabla siempre matche.',
          '- Devolvé SOLO el outline corregido (Markdown). Sin prosa, sin fences.',
        ].join('\n');

    const payload = `${header}\n\n--- OUTLINE ACTUAL ---\n${this._outlineText}`
                  + `\n\n--- PROBLEMAS A CORREGIR ---\n${issuesText}`;

    this._fixing = true;
    this._refreshApplyButton();

    this._goStep(5);
    if (this._abortCtl) try { this._abortCtl.abort(); } catch (_) { /* noop */ }
    this._abortCtl = new AbortController();

    try {
      const result = await dmnOutline(
        {
          source: 'text',
          payload,
          language: this._language,
          include_scenarios: true,
          document_id: null,
        },
        this._abortCtl.signal,
      );
      this._applyOutlineResult(result);
      this._goStep(2);
    } catch (err) {
      if (err.name === 'AbortError') { this._goStep(2); return; }
      this._goStep(2);
      alert(`No se pudo arreglar el outline: ${err.message || err}`);
    } finally {
      this._abortCtl = null;
      this._fixing   = false;
      this._refreshApplyButton();
    }
  }

  // ── Apply ───────────────────────────────────────────────────────────────

  /**
   * Top-level Apply handler. Only **errors** open the confirm overlay
   * ("¿corregir antes o aplicar igualmente?"). Warnings and info are
   * advisory and don't block — they're already visible in the issues panel.
   */
  async _apply() {
    if (!this._outlineText.trim()) {
      alert('No hay outline para aplicar.');
      return;
    }
    const hasErrors = (this._lintSummary.errors || 0) > 0;
    if (hasErrors) {
      this._openApplyConfirm();
      return;
    }
    return this._doApply();
  }

  _openApplyConfirm() {
    const overlay = document.getElementById('ai-dmn-gen-apply-confirm');
    const body    = document.getElementById('ai-dmn-gen-apply-confirm-body');
    if (!overlay || !body) {
      // Fallback if the confirm overlay isn't in the DOM (legacy embed): just go.
      return this._doApply();
    }
    const { groups, hidden } = groupFriendly(this._lintIssues, this._language);
    const parts = [];
    parts.push(`<p class="ai-dmn-gen-apply-confirm__lead">${
      this._language === 'en'
        ? 'There are open points before applying:'
        : 'Quedan puntos sin resolver antes de aplicar:'
    }</p>`);
    if (groups.length === 0 && hidden > 0) {
      parts.push(`<p>${
        this._language === 'en'
          ? `${hidden} technical issue${hidden === 1 ? '' : 's'}.`
          : `${hidden} observación${hidden === 1 ? '' : 'es'} técnica${hidden === 1 ? '' : 's'}.`
      }</p>`);
    } else {
      parts.push('<ul class="ai-dmn-gen-apply-confirm__list">');
      for (const g of groups) {
        for (const f of g.issues) {
          parts.push(`<li><strong>${_esc(g.title)}</strong> — ${_esc(f.title)}</li>`);
        }
      }
      parts.push('</ul>');
      if (hidden > 0) {
        parts.push(`<p class="ai-dmn-gen-apply-confirm__hidden">${
          this._language === 'en'
            ? `+ ${hidden} technical detail${hidden === 1 ? '' : 's'} (visible in "Detalles técnicos").`
            : `+ ${hidden} detalle${hidden === 1 ? '' : 's'} técnico${hidden === 1 ? '' : 's'} (visibles en "Detalles técnicos").`
        }</p>`);
      }
    }
    body.innerHTML = parts.join('\n');
    overlay.hidden = false;
  }

  _closeApplyConfirm() {
    const overlay = document.getElementById('ai-dmn-gen-apply-confirm');
    if (overlay) overlay.hidden = true;
  }

  async _doApply() {
    if (!this._outlineText.trim()) {
      alert('No hay outline para aplicar.');
      return;
    }
    console.log('[DmnGenerateModal] _doApply: requesting XML from backend');

    let xmlResp;
    try {
      xmlResp = await dmnOutlineToXml(this._outlineText, this._language);
    } catch (err) {
      console.error('[DmnGenerateModal] dmnOutlineToXml failed:', err);
      alert(`No se pudo generar el XML: ${err.message || err}`);
      return;
    }
    console.log('[DmnGenerateModal] backend response:', {
      hasXml: !!xmlResp?.dmn_xml,
      xmlLen: xmlResp?.dmn_xml?.length || 0,
      blocked: !!xmlResp?.metadata?.blocked,
      issues: (xmlResp?.issues || []).length,
    });
    // The backend's `blocked` flag is no longer fatal here — the user has
    // chosen to apply with errors and we surface that decision via a chat
    // notice. Only bail when the backend genuinely produced no XML to import.
    if (!xmlResp.dmn_xml) {
      const beIssues = (xmlResp.issues || []).map((i) => `• ${i.message || i.code}`).join('\n');
      alert('El backend no pudo materializar XML para este outline.\n\n'
          + (beIssues ? `Detalle:\n${beIssues}\n\n` : '')
          + 'Revisá los errores en el panel "Cosas a revisar" o usá "🛠 Arreglar errores".');
      return;
    }
    const wasBlocked = !!xmlResp.metadata?.blocked;

    const dmn = this._epxDmn || window.epxDmn;
    if (!dmn || typeof dmn.importXML !== 'function') {
      console.error('[DmnGenerateModal] DMN editor not available; epxDmn=', dmn);
      alert('El editor DMN no está disponible.');
      return;
    }

    try {
      console.log('[DmnGenerateModal] importing XML into editor (', xmlResp.dmn_xml.length, 'chars )');
      await dmn.importXML(xmlResp.dmn_xml);
      console.log('[DmnGenerateModal] importXML OK');
    } catch (err) {
      console.error('[DmnGenerateModal] importXML failed:', err);
      alert(`No se pudo aplicar el DMN: ${err.message || err}`);
      return;
    }

    let summary = wasBlocked
      ? '⚠ DMN aplicado al editor con issues sin resolver — revisá las decisiones marcadas.'
      : '✓ DMN aplicado al editor.';
    const scenarios = this._lastOutline?.scenarios || [];
    if (this._runAfterApply && scenarios.length > 0) {
      try {
        if (dmn.simulator?.open) dmn.simulator.open();
        const loaded = dmn.simulator?.loadScenarios?.(scenarios);
        const ran    = dmn.simulator?.runAll?.();
        const ok     = Array.isArray(ran) ? ran.filter((r) => r && r.passed !== false).length : null;
        const total  = Array.isArray(ran) ? ran.length : scenarios.length;
        summary += loaded
          ? `\n▶ Simulador: ${ok != null ? `${ok}/${total} escenarios pasaron` : `${total} escenario(s) cargado(s)`}.`
          : '';
      } catch (err) {
        console.warn('[DmnGenerateModal] simulator failed:', err);
      }
    }

    if (this._panel && typeof this._panel.addAiMessage === 'function') {
      this._panel.addAiMessage(summary);
    }
    if (typeof this._onApply === 'function') {
      try { this._onApply({ xml: xmlResp.dmn_xml, scenarios }); } catch (_) { /* noop */ }
    }
    this.close();
  }

  async transcribeVoice(blob) {
    return transcribe(blob);
  }
}

export default DmnGenerateModal;
